import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import classNames from 'classnames';
import { Button } from 'fluxo-ui';
import { BrightSunIcon, ChevronDownIcon, LogoutIcon, MenuIcon, NightLightIcon, SearchIcon, SettingsIcon, UserIcon } from 'fluxo-ui/icons';

import { ROUTES } from '../../common/constants';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';

import Logo from './Logo';

interface HeaderProps {
    showSidebarToggle?: boolean;
}

const PAGE_TITLES: Record<string, string> = {
    dashboard: 'Dashboard',
    questions: 'Question Bank',
    'questions/create': 'Create Question',
    'questions/pending': 'Pending Approvals',
    'questions/flags': 'Reported Issues',
    exams: 'Exams',
    'exams/create': 'Create Exam',
    'live-quiz': 'Live Quiz',
    categories: 'Categories',
    analytics: 'Analytics',
    organizations: 'Organizations',
    organization: 'Organization',
    users: 'Users',
    resources: 'Resources',
    settings: 'Settings',
    profile: 'Profile',
    results: 'Results',
    certificates: 'Certificates',
};

const iconButtonClasses =
    'flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors';

const menuItemClasses =
    'flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors';

const isMacPlatform = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

const Header: React.FC<HeaderProps> = ({ showSidebarToggle = false }) => {
    const { isAuthenticated, user, logout } = useAuthStore();
    const { theme, toggleTheme, toggleSidebar, setCommandPaletteOpen } = useUIStore();
    const navigate = useNavigate();
    const location = useLocation();
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowMenu(false);
            }
        };
        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMenu]);

    useEffect(() => {
        setShowMenu(false);
    }, [location.pathname]);

    const handleLogout = () => {
        setShowMenu(false);
        logout();
        window.location.href = ROUTES.LOGIN;
    };

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const pageTitle = PAGE_TITLES[pathSegments.slice(0, 2).join('/')] || PAGE_TITLES[pathSegments[0] || ''];

    const userName = user?.profile?.name || user?.name || user?.email || 'User';
    const userRole = user?.role?.replace(/_/g, ' ') || '';
    const userInitial = String(userName).charAt(0).toUpperCase();

    return (
        <header className="sticky top-0 z-40 bg-white/85 dark:bg-slate-900/85 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80 transition-colors">
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between gap-3 h-14">
                    {/* Left side */}
                    <div className="flex items-center gap-2 min-w-0">
                        {showSidebarToggle && (
                            <button onClick={toggleSidebar} aria-label="Toggle sidebar" className={classNames(iconButtonClasses, 'lg:hidden -ml-1.5')}>
                                <MenuIcon className="w-5 h-5" />
                            </button>
                        )}

                        {/* Brand — on the authenticated shell the sidebar owns the brand on large screens */}
                        <Link
                            to={isAuthenticated ? ROUTES.DASHBOARD : ROUTES.HOME}
                            className={classNames('flex items-center gap-2.5 group shrink-0', { 'lg:hidden': showSidebarToggle })}
                        >
                            <Logo className="w-8 h-8 rounded-lg shadow-sm group-hover:shadow-md transition-shadow" />
                            <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Testam</span>
                        </Link>

                        {/* Page title (authenticated shell, large screens) */}
                        {showSidebarToggle && pageTitle && (
                            <h1 className="hidden lg:block text-[15px] font-semibold text-slate-900 dark:text-white truncate">
                                {pageTitle}
                            </h1>
                        )}
                    </div>

                    {/* Right side */}
                    <div className="flex items-center gap-1.5 shrink-0">
                        {isAuthenticated && (
                            <>
                                {/* Search / command palette trigger */}
                                <button
                                    onClick={() => setCommandPaletteOpen(true)}
                                    aria-label="Search"
                                    className="hidden sm:flex items-center gap-2 h-9 pl-3 pr-2 mr-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-sm text-slate-400 dark:text-slate-500 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-600 dark:hover:text-slate-300 transition-colors w-44 lg:w-56"
                                >
                                    <SearchIcon className="w-4 h-4 shrink-0" />
                                    <span className="flex-1 text-left">Search...</span>
                                    <kbd className="px-1.5 py-0.5 text-[10px] font-semibold rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-400 dark:text-slate-500">
                                        {isMacPlatform ? '⌘K' : 'Ctrl K'}
                                    </kbd>
                                </button>
                                <button
                                    onClick={() => setCommandPaletteOpen(true)}
                                    aria-label="Search"
                                    className={classNames(iconButtonClasses, 'sm:hidden')}
                                >
                                    <SearchIcon className="w-5 h-5" />
                                </button>
                            </>
                        )}

                        {/* Theme toggle */}
                        <button onClick={toggleTheme} aria-label="Toggle theme" className={iconButtonClasses}>
                            {theme === 'light' ? <NightLightIcon className="w-5 h-5" /> : <BrightSunIcon className="w-5 h-5" />}
                        </button>

                        {isAuthenticated && user ? (
                            <div className="relative" ref={menuRef}>
                                <span aria-hidden className="hidden sm:inline-block w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1.5 align-middle" />
                                <button
                                    onClick={() => setShowMenu(!showMenu)}
                                    aria-label="User menu"
                                    aria-expanded={showMenu}
                                    className="inline-flex items-center gap-2 h-10 pl-1.5 pr-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors align-middle"
                                >
                                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600 text-white text-xs font-semibold">
                                        {userInitial}
                                    </span>
                                    <span className="hidden md:block max-w-[10rem] text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                                        {userName}
                                    </span>
                                    <ChevronDownIcon
                                        className={classNames('w-3.5 h-3.5 text-slate-400 transition-transform duration-150', {
                                            'rotate-180': showMenu,
                                        })}
                                    />
                                </button>

                                {showMenu && (
                                    <div className="absolute right-0 top-12 w-60 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl shadow-slate-200/60 dark:shadow-black/40 p-1.5 z-50">
                                        <div className="flex items-center gap-3 px-3 py-2.5 mb-1 rounded-lg bg-slate-50 dark:bg-slate-800/60">
                                            <span className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600 text-white text-sm font-semibold">
                                                {userInitial}
                                            </span>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{userName}</p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400 capitalize truncate">{userRole}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                navigate(ROUTES.PROFILE);
                                            }}
                                            className={menuItemClasses}
                                        >
                                            <UserIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                                            Profile
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowMenu(false);
                                                navigate(ROUTES.SETTINGS);
                                            }}
                                            className={menuItemClasses}
                                        >
                                            <SettingsIcon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                                            Settings
                                        </button>
                                        <div className="border-t border-slate-100 dark:border-slate-800 my-1.5" />
                                        <button
                                            onClick={handleLogout}
                                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <LogoutIcon className="w-4 h-4" />
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 ml-1.5">
                                <Button label="Login" variant="default" layout="outlined" size="sm" onClick={() => navigate(ROUTES.LOGIN)} />
                                <Button label="Sign Up" variant="primary" size="sm" onClick={() => navigate(ROUTES.SIGNUP)} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
